import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import WorkbrookTab from "./WorkbrookTab";
import CommunityTab from "./CommunityTab";
import { useCommunityPosts } from "../../../../hooks/queries/communityQueries";
import "./feedTabs.css";

/**
 * FeedTabs — two-tab container for the rebuilt Feed.
 *
 * Tab 1: "Workbrook" (default) — platform-curated activity cards,
 *         leaderboard, editorial content. Phase 1: empty state.
 * Tab 2: "Community" — user-generated social feed (composer + posts).
 *
 * Requirements from PRD §7.1:
 *   - Workbrook tab is the default landing tab
 *   - Tabs persist scroll position independently
 *   - Tab state is retained during the session
 *
 * Tab state is persisted in sessionStorage so it survives re-renders
 * and route changes within the session but resets on a fresh visit.
 * Scroll positions are tracked per-tab via refs and restored on switch.
 *
 * Each tab shows an item-count badge. Per PRD §10, a count of 0 is
 * never displayed — the badge is hidden when there are no items.
 */

const TAB_WORKBROOK = "workbrook";
const TAB_COMMUNITY = "community";
const SESSION_KEY = "feed_active_tab";

const FeedTabs = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || TAB_WORKBROOK;
    } catch {
      return TAB_WORKBROOK;
    }
  });

  // Sprint 3: deep-link from notification bell icon
  // Handles: /league?tab=community&post=123, /league?card=456
  const [highlightCardId, setHighlightCardId] = useState(null);
  const [highlightPostId, setHighlightPostId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const cardId = params.get("card");
    const postId = params.get("post");

    if (tab === "community") {
      setActiveTab(TAB_COMMUNITY);
      if (postId) setHighlightPostId(postId);
    } else if (cardId) {
      setActiveTab(TAB_WORKBROOK);
      setHighlightCardId(cardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Community post count — from TanStack Query cache.
  const { data: communityPosts = [] } = useCommunityPosts(1);
  const communityCount = communityPosts.length;

  // Workbrook card count — read from the feedCard reducer.
  const feedCardsState = useSelector((store) => store.feedCards);
  const workbrookCount = feedCardsState?.cards?.length || 0;

  // Track scroll position per tab so switching back restores position.
  // Scroll is on the .feed-tab-content container (not the window) so the
  // right sidebar stays independent.
  const scrollPositions = useRef({
    [TAB_WORKBROOK]: 0,
    [TAB_COMMUNITY]: 0,
  });
  const scrollContainerRef = useRef(null);

  // Persist tab choice in sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, activeTab);
    } catch {
      // sessionStorage may be unavailable (private mode); fail silently
    }
  }, [activeTab]);

  // Save scroll position before switching tabs, restore after
  const handleTabSwitch = useCallback(
    (newTab) => {
      if (newTab === activeTab) return;

      // Save current scroll position from the container
      if (scrollContainerRef.current) {
        scrollPositions.current[activeTab] = scrollContainerRef.current.scrollTop;
      }

      setActiveTab(newTab);

      // Restore scroll position for the newly active tab
      // (deferred to next paint so the panel is rendered first)
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollPositions.current[newTab] || 0;
        }
      });
    },
    [activeTab]
  );

  const switchToCommunity = useCallback(() => {
    handleTabSwitch(TAB_COMMUNITY);
  }, [handleTabSwitch]);

  return (
    <div className="feed-tabs-container">
      {/* ── Tab Bar ─────────────────────────────────────────────── */}
      <div className="feed-tab-bar">
        <button
          className={`feed-tab-button ${
            activeTab === TAB_WORKBROOK ? "feed-tab-active" : ""
          }`}
          onClick={() => handleTabSwitch(TAB_WORKBROOK)}
          aria-selected={activeTab === TAB_WORKBROOK}
          role="tab"
        >
          workbrook
          {workbrookCount > 0 && (
            <span className="feed-tab-badge">{workbrookCount}</span>
          )}
        </button>
        <button
          className={`feed-tab-button ${
            activeTab === TAB_COMMUNITY ? "feed-tab-active" : ""
          }`}
          onClick={() => handleTabSwitch(TAB_COMMUNITY)}
          aria-selected={activeTab === TAB_COMMUNITY}
          role="tab"
        >
          community
          {communityCount > 0 && (
            <span className="feed-tab-badge">{communityCount}</span>
          )}
        </button>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────── */}
      <div className="feed-tab-content" ref={scrollContainerRef}>
        <div
          className={`feed-tab-panel ${
            activeTab === TAB_WORKBROOK ? "feed-tab-active" : ""
          }`}
          role="tabpanel"
          hidden={activeTab !== TAB_WORKBROOK}
        >
          {activeTab === TAB_WORKBROOK && (
            <WorkbrookTab
              onSwitchToCommunity={switchToCommunity}
              highlightCardId={highlightCardId}
            />
          )}
        </div>

        <div
          className={`feed-tab-panel ${
            activeTab === TAB_COMMUNITY ? "feed-tab-active" : ""
          }`}
          role="tabpanel"
          hidden={activeTab !== TAB_COMMUNITY}
        >
          {activeTab === TAB_COMMUNITY && (
            <CommunityTab highlightPostId={highlightPostId} />
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedTabs;
